import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardHeader,
  CardContent,
  Divider,
  useTheme,
  Tooltip,
  Chip,
  IconButton,
  Container,
  CircularProgress,
} from "@mui/material";
import { useQuery, useMutation } from "@apollo/client";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { Archive as ArchiveIcon, Unarchive as UnarchiveIcon } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import AddExternalUserDialog from "./AddExternalUserDialog";
import DeactivateUserDialog from "./DeactivateUserDialog";
import EditAccessDateDialog from "./EditAccessDateDialog";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import InformationStepsCard from "../../../../components/InformationStepsCard";
import {
  GET_EXTERNAL_USERS,
  UPDATE_EXTERNAL_USER_ACCESS,
} from "../../../../apis/organization-apis/people-apis/externalUserApis";
import { formatDate } from "../../../../utils/dateUtils";
import { Slide } from "@mui/material";
import { exportExternalUsersToExcel } from "./utils/exportToExcel";
import { ExternalUserStatus, ExternalUserStatusLabel } from "../../../../utils/enums";

const statusFilterOptions = [
  { id: ExternalUserStatus.ACTIVE, label: ExternalUserStatusLabel.ACTIVE },
  { id: ExternalUserStatus.INACTIVE, label: ExternalUserStatusLabel.INACTIVE },
];

const ExternalUsersList = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableFilters, setTableFilters] = useState({});
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [reactivatingKey, setReactivatingKey] = useState(null);
  const [editDateDialogOpen, setEditDateDialogOpen] = useState(false);
  const [dateToEdit, setDateToEdit] = useState(null);
  const [dateTypeToEdit, setDateTypeToEdit] = useState(null);

  const { data, loading, refetch } = useQuery(GET_EXTERNAL_USERS, {
    variables: {
      page: page + 1,
      limit: rowsPerPage,
    },
    fetchPolicy: "no-cache",
  });

  const [updateExternalUserAccess] = useMutation(UPDATE_EXTERNAL_USER_ACCESS, {
    onCompleted: async (res) => {
      const ok = res?.updateExternalUserAccess?.success;
      if (ok) {
        setIsDeactivating(false);
        setDeactivateDialogOpen(false);
        setUserToDeactivate(null);
        setReactivatingKey(null);
        await refetch();
        setSnackbarInfo({
          message: res?.updateExternalUserAccess?.message || "External user access updated successfully",
          vertical: "top",
          horizontal: "right",
          Transition: Slide,
        });
      } else {
        setIsDeactivating(false);
        setReactivatingKey(null);
        setSnackbarInfo({
          message:
            res?.updateExternalUserAccess?.message ||
            "Failed to update external user access",
          vertical: "top",
          horizontal: "right",
          Transition: Slide,
        });
      }
    },
    onError: (error) => {
      setIsDeactivating(false);
      setReactivatingKey(null);
      console.error("Error updating external user access:", error);
      setSnackbarInfo({
        message: error?.message || "Failed to update external user access",
        vertical: "top",
        horizontal: "right",
        Transition: Slide,
      });
    },
  });


  const externalUsers = useMemo(() => {
    return data?.getExternalUsers?.data || [];
  }, [data]);

  const totalCount = data?.getExternalUsers?.totalCount || 0;

  const handleDeactivateClick = (user) => {
    setUserToDeactivate(user);
    setDeactivateDialogOpen(true);
  };

  const handleConfirmDeactivate = () => {
    if (userToDeactivate) {
      setIsDeactivating(true);
      updateExternalUserAccess({
        variables: {
          input: {
            external_user_id: userToDeactivate._originalUser?.id || userToDeactivate.id,
            role_id: userToDeactivate._originalUser?.role_id,
            is_active: false,
          },
        },
      });
    }
  };

  const handleReactivate = (user) => {
    if (!user?.id || !user?.role_id) return;
    const key = `${user.id}-${user.role_id}`;
    // Prevent duplicate clicks for the same access while request is in-flight
    if (reactivatingKey === key) return;
    setReactivatingKey(key);
    updateExternalUserAccess({
      variables: {
        input: {
          external_user_id: user.id,
          role_id: user.role_id,
          is_active: true,
        },
      },
    });
  };


  const handleExportToExcel = () => {
    if (tableData?.length > 0) {
      exportExternalUsersToExcel(tableData);
    }
  };

  const handleFilterChange = (filters) => {
    setTableFilters(filters);
  };

  const filteredUsers = useMemo(() => {
    let filtered = externalUsers;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          user.name?.toLowerCase()?.includes(query) ||
          user.email?.toLowerCase()?.includes(query) ||
          user.role_name?.toLowerCase()?.includes(query) ||
          user.granted_by_email?.toLowerCase()?.includes(query)
      );
    }

    // Apply status filter - use is_access_active from backend
    if (tableFilters.status && tableFilters.status.length > 0) {
      filtered = filtered.filter((user) => {
        const userStatus = user.is_access_active ? ExternalUserStatus.ACTIVE : ExternalUserStatus.INACTIVE;
        return tableFilters.status.includes(userStatus);
      });
    }

    return filtered;
  }, [externalUsers, searchQuery, tableFilters]);

  const tableData = useMemo(() => {
    return filteredUsers.map((user) => {
      const isActive = user.is_access_active ?? false;
      return {
        id: user.id,
        name: user.name || "-",
        email: user.email || "-",
        role: user.role_name || "-",
        grantedBy: user.granted_by_email || "-",
        accessFrom: user.access_from ? formatDate(user.access_from) : "Not set",
        accessTill: user.access_till ? formatDate(user.access_till) : "Not set",
        status: isActive ? ExternalUserStatus.ACTIVE : ExternalUserStatus.INACTIVE,
        statusLabel: isActive
          ? ExternalUserStatusLabel.ACTIVE
          : ExternalUserStatusLabel.INACTIVE,
        isActive: user.is_active,
        accessFromRaw: user.access_from,
        accessTillRaw: user.access_till,
        _originalUser: user,
      };
    });
  }, [filteredUsers]);

  const baseColumns = [
    {
      label: "Person",
      field: "person",
      width: "18%",
      align: "left",
    },
    {
      label: "Staff Role",
      field: "role",
      width: "12%",
      align: "left",
    },
    {
      label: "Access Granted By",
      field: "grantedBy",
      width: "22%",
      align: "left",
    },
    {
      label: "Access From",
      field: "accessFrom",
      width: "12%",
      align: "left",
    },
    {
      label: "Access Till",
      field: "accessTill",
      width: "12%",
      align: "left",
    },
    {
      label: "Status",
      field: "status",
      width: "10%",
      align: "left",
      filterConfig: {
        filterOptions: statusFilterOptions,
      },
    },
  ];

  const actionColumn = [
    {
      label: "",
      field: "action",
      width: "6%",
      align: "left",
    },
  ];

  const columns = [...baseColumns, ...actionColumn];

  const renderCell = (row, column) => {
    switch (column?.field) {
      case "person":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: 0,
              width: "100%",
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap" }}
              >
                <Tooltip title={row?.name || ""} arrow>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{
                      color: theme.customColors.text.strong,
                      textAlign: "left",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row?.name}
                  </Typography>
                </Tooltip>
              </Box>
              <Tooltip title={row?.email || ""} arrow>
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.customColors.text.secondary,
                    textAlign: "left",
                    width: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row?.email}
                </Typography>
              </Tooltip>
            </Box>
          </Box>
        );
      case "role":
        const hasRole = row?.role && row?.role !== "-" && row?.role !== "Not Assigned";
        return (
          <Box
            sx={{
              textAlign: "left",
              width: "100%",
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Tooltip title={row?.role === "-" ? "Not Assigned" : row?.role} arrow>
              <Chip
                label={row?.role === "-" ? "Not Assigned" : row?.role}
                size="small"
                sx={{
                  bgcolor: hasRole ? "rgba(26,86,219,0.08)" : "background.paper",
                  color: hasRole ? "primary.main" : "text.secondary",
                  fontWeight: 500,
                  fontSize: "0.75rem",
                  height: "24px",
                  borderRadius: 1,
                  ...(row?.role !== "-" && { px: 1.5 }),
                  boxShadow: "none",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  },
                }}
              />
            </Tooltip>
          </Box>
        );
      case "grantedBy":
        return (
          <Tooltip title={row?.grantedBy || "-"} arrow>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%",
              }}
            >
              {row?.grantedBy || "-"}
            </Typography>
          </Tooltip>
        );
      case "accessFrom":
        return (
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.strong,
              textAlign: "left",
            }}
          >
            {row?.accessFrom || "-"}
          </Typography>
        );
      case "accessTill":
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                color: theme.customColors.text.strong,
                textAlign: "left",
              }}
            >
              {row?.accessTill || "-"}
            </Typography>
            <Tooltip title="Edit Access Till Date" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    setDateToEdit(row);
                    setDateTypeToEdit("access_till");
                    setEditDateDialogOpen(true);
                  }}
                  sx={{
                    p: 0.25,
                    color: "#64748b",
                    "&:hover": { color: "#031B89", backgroundColor: "#f0f4ff" },
                  }}
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      case "status":
        const isActive = row.status === ExternalUserStatus.ACTIVE;
        return (
          <Box sx={{ textAlign: "left", width: "100%" }}>
            <Chip
              label={row.statusLabel || (isActive ? ExternalUserStatusLabel.ACTIVE : ExternalUserStatusLabel.INACTIVE)}
              size="small"
              sx={{
                backgroundColor: isActive
                  ? theme.customColors.status.success.bg
                  : theme.customColors.status.error.bg,
                color: isActive
                  ? theme.customColors.status.success.text
                  : theme.customColors.status.error.text,
                minWidth: "90px",
                textAlign: "center",
                borderRadius: 0,
                fontWeight: 600,
                fontSize: "0.75rem",
                border: "none",
                height: "24px",
              }}
            />
          </Box>
        );
      case "action":
        const isInactive = row.status === ExternalUserStatus.INACTIVE;
        const isManuallyInactive = isInactive && row.isActive === false;
        const isInactiveDueToDates = isInactive && row.isActive !== false;
        const rowOriginal = row._originalUser || row;
        const rowReactivationKey =
          rowOriginal?.id && rowOriginal?.role_id
            ? `${rowOriginal.id}-${rowOriginal.role_id}`
            : null;
        const isRowReactivating =
          !!rowReactivationKey && reactivatingKey === rowReactivationKey;
        
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            {!isInactive ? (
              <Tooltip title="Deactivate access" arrow>
                <IconButton
                  size="small"
                  onClick={() => handleDeactivateClick(row)}
                  sx={{
                    color: "#DC2626",
                    width: 40,
                    height: 40,
                    "&:hover": {
                      backgroundColor: theme.customColors.status.error.bg,
                    },
                  }}
                >
                  <ArchiveIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                title={
                  isManuallyInactive
                    ? "Reactivate access"
                    : "Inactive due to access dates. Update Access Till to reactivate."
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => handleReactivate(row._originalUser)}
                    disabled={!isManuallyInactive || isRowReactivating}
                    sx={{
                      color:
                        isManuallyInactive && !isRowReactivating
                          ? "#10B981"
                          : theme.customColors.text.disabled,
                      width: 40,
                      height: 40,
                      "&:hover": {
                        backgroundColor:
                          isManuallyInactive && !isRowReactivating
                            ? "rgba(16, 185, 129, 0.1)"
                            : "transparent",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                        cursor: "not-allowed",
                      },
                    }}
                  >
                    <UnarchiveIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      default:
        return (
          <Typography variant="body2" sx={{ color: theme.customColors.text.strong }}>
            {row[column?.field] || "-"}
          </Typography>
        );
    }
  };

  if (loading) {
    return (
      <Container
        sx={{
          height: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </Container>
    );
  }

  return (
    <Box
      sx={{ width: "100%", pl: 0, pr: 0, ml: 0, mr: 0, mt: 2 }}
      data-id="external-users-list-container"
    >
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps you manage and track external users who have been granted access to your organization."
          steps={[
            "**View all external users** who have been granted access to your organization.",
            "**Monitor access periods** for external users with start and end dates.",
            "**Track who granted access** to each external user for audit purposes.",
            "**Manage external user access** by activating or deactivating their permissions as needed.",
            "**Add new external users** and assign appropriate roles and access periods."
          ]}
          footerText="💡 Regularly reviewing external user access helps maintain security and ensures only authorized personnel have access to your organization's data."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      {/* External Users List Section */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            <Typography
              variant="h6"
              fontWeight="600"
              color={theme.customColors.text.strong}
            >
              External Users
            </Typography>
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: "16px",
              fontWeight: "bold",
            },
            padding: "14px",
          }}
          action={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Tooltip title="Add External User" arrow>
                <span>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => setAddDialogOpen(true)}
                    sx={{
                      bgcolor: theme.customColors.button.primary,
                      color: theme.customColors.button.text,
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      "&:hover": {
                        bgcolor: theme.palette._blue?.dark || theme.customColors.button.primary,
                      },
                    }}
                  >
                    Add External User
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Download external users data as Excel" arrow>
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    disabled={!tableData?.length}
                    onClick={handleExportToExcel}
                    sx={{
                      fontWeight: 500,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontSize: "14px",
                      px: 2,
                      py: 1,
                      borderColor: theme.customColors.border.main,
                      color: theme.customColors.text.strong,
                      "&:hover": {
                        borderColor: theme.customColors.border.strong,
                        backgroundColor: theme.customColors.background.hover,
                      },
                      "&:disabled": {
                        borderColor: theme.customColors.border.disabled,
                        color: theme.customColors.text.disabled,
                      },
                    }}
                  >
                    Export
                  </Button>
                </span>
              </Tooltip>
            </Box>
          }
        />

        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 0,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 220 }}>
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#6B7280", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchQuery("")}
                          edge="end"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      transition: "all 0.2s ease",
                      fontSize: "14px",
                      "& fieldset": {
                        borderColor: "#E5E7EB",
                      },
                      "&:hover fieldset": {
                        borderColor: "#9CA3AF",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#3B82F6",
                        boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 120, display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <Box
                  sx={{
                    px: 3,
                    py: 1,
                    borderRadius: "10px",
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #E5E7EB",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="#374151" fontWeight="500" sx={{ lineHeight: 1.2 }}>
                    {tableData?.length}{" "}
                    {tableData?.length === 1 ? "user" : "users"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          <Box>
            <ReusableTable
              columns={columns}
              data={tableData}
              renderCell={renderCell}
              loading={loading}
              rowsPerPageOptions={[10, 50, 100]}
              rowsPerPage={50}
              onFilterChange={handleFilterChange}
              filters={tableFilters}
              sx={{
                "& .MuiTableCell-root": {
                  py: 1.5,
                  px: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
                "& .MuiTableRow-root:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.01)",
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <AddExternalUserDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          setAddDialogOpen(false);
          refetch();
          setSnackbarInfo({
            message: "External user added successfully",
            vertical: "top",
            horizontal: "right",
            Transition: Slide,
          });
        }}
      />

      <DeactivateUserDialog
        open={deactivateDialogOpen}
        onClose={() => {
          setDeactivateDialogOpen(false);
          setUserToDeactivate(null);
        }}
        onConfirm={handleConfirmDeactivate}
        userName={userToDeactivate?.name}
        roleName={
          userToDeactivate?._originalUser?.role_name || userToDeactivate?.role
        }
        loading={isDeactivating}
      />

      <EditAccessDateDialog
        open={editDateDialogOpen}
        onClose={() => {
          setEditDateDialogOpen(false);
          setDateToEdit(null);
          setDateTypeToEdit(null);
        }}
        user={dateToEdit?._originalUser}
        dateType={dateTypeToEdit}
        currentDate={
          dateTypeToEdit === "access_till"
            ? dateToEdit?.accessTillRaw
            : dateToEdit?.accessFromRaw
        }
        refetch={async () => {
          await refetch({
            page: page + 1,
            limit: rowsPerPage,
            search: searchQuery || undefined,
          });
        }}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default ExternalUsersList;
